import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

/** The two panels of "Moteurs d'IA", in their displayed order. */
export type AiEnginesTab = 'providers' | 'models';

const TAB_QUERY_PARAM = 'tab';

type UseAiEnginesTabResult = {
	readonly activeIndex: number;
	readonly setActiveIndex: (index: number) => void;
};

/**
 * Reads and writes the `tab` query parameter. An absent, unrecognized or unavailable value (the tab
 * exists only under its read permission) resolves to the first available tab, so a bare reload or a
 * shared link always lands somewhere valid. `activeIndex` is positional within `availableTabs`.
 */
const useAiEnginesTab = (availableTabs: readonly AiEnginesTab[]): UseAiEnginesTabResult => {
	const [searchParams, setSearchParams] = useSearchParams();

	const activeIndex = Math.max(
		availableTabs.findIndex((tab) => tab === searchParams.get(TAB_QUERY_PARAM)),
		0,
	);

	const setActiveIndex = React.useCallback(
		(index: number) => {
			const nextTab = availableTabs[index];
			if (nextTab === undefined) {
				return;
			}

			setSearchParams(
				(current) => {
					const next = new URLSearchParams(current);
					next.set(TAB_QUERY_PARAM, nextTab);
					return next;
				},
				{ replace: true },
			);
		},
		[availableTabs, setSearchParams],
	);

	return { activeIndex, setActiveIndex };
};

export default useAiEnginesTab;
